import React, { useEffect, useState } from "react";
import { UserCog, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Red persistent bar shown across the top of every page when an admin is
 * currently piloting another user's account via a 15-minute impersonation
 * token. Reading this file? It's intentionally not-dismissible — the exit
 * button clears the token and restores the admin's real session (stashed
 * away when impersonation started).
 *
 * Contract with the admin drill-down UI:
 *   - Before jumping in, save the admin's own token under `dpp_admin_token_backup`
 *   - Set the impersonation token as `dpp_token`
 *   - Set `dpp_impersonation` = { target_email, impersonated_by, expires_at }
 */
const KEY = "dp_impersonation";
const ADMIN_TOKEN_BACKUP = "dp_admin_token_backup";

export default function ImpersonationBanner() {
  const { user, refresh } = useAuth();
  const [meta, setMeta] = useState(null);
  const [remaining, setRemaining] = useState(null);

  // Poll storage every second so a manual clear elsewhere still updates the UI.
  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem(KEY);
        setMeta(raw ? JSON.parse(raw) : null);
      } catch { setMeta(null); }
    };
    read();
    const t = setInterval(read, 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-expire countdown.
  useEffect(() => {
    if (!meta?.expires_at) { setRemaining(null); return; }
    const tick = () => {
      const ms = new Date(meta.expires_at).getTime() - Date.now();
      if (ms <= 0) {
        exitImpersonation();
      } else {
        setRemaining(Math.round(ms / 1000));
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.expires_at]);

  const exitImpersonation = async () => {
    const backup = localStorage.getItem(ADMIN_TOKEN_BACKUP);
    localStorage.removeItem(KEY);
    if (backup) {
      localStorage.setItem("dp_token", backup);
      localStorage.removeItem(ADMIN_TOKEN_BACKUP);
    }
    setMeta(null);
    toast.success("Exited impersonation — restored admin session");
    // Force AuthContext to re-hydrate from the swapped token.
    if (typeof refresh === "function") await refresh();
    // Full reload as a safety net so cached user-scoped state (portfolios,
    // holdings, chatbot history) is dropped.
    window.location.href = "/admin";
  };

  if (!meta) return null;

  const mm = remaining != null ? Math.floor(remaining / 60) : "--";
  const ss = remaining != null ? String(remaining % 60).padStart(2, "0") : "--";

  return (
    <div
      className="bg-red-600 text-white px-4 sm:px-6 py-2 border-b border-red-800 shadow-md"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-3 text-sm">
        <UserCog className="w-4 h-4 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="mono text-[10px] uppercase tracking-widest opacity-90">Impersonating</span>{" "}
          <span className="font-semibold truncate">{meta.target_email || user?.email}</span>
          <span className="opacity-90 ml-2">· admin session belongs to <span className="font-semibold">{meta.impersonated_by}</span></span>
          <span className="opacity-80 ml-2 mono text-[11px]">· auto-exit in {mm}:{ss}</span>
        </div>
        <button
          type="button"
          onClick={exitImpersonation}
          data-testid="impersonation-exit"
          className="mono text-[10px] uppercase tracking-widest bg-white/20 hover:bg-white/30 transition-colors px-3 py-1 rounded-full inline-flex items-center gap-1.5"
        >
          <X className="w-3 h-3" /> Exit
        </button>
      </div>
    </div>
  );
}
